/**
 * @file BenefitPlanTabsPage.js
 * @description Main component for managing benefit plan tabs and handling plan data.
 */

import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './PlanYear.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import BenefitPlanOverviewTab from './tabs/BenefitPlanOverviewTab';
import BenefitPlanHeaderDisplayTab from './tabs/BenefitPlanHeaderDisplayTab';
import BenefitPlanFilesTab from './tabs/BenefitPlanFilesTab';
import BenefitPlanDetailsTab, {
    VALUE_TYPE,
} from './tabs/BenefitPlanDetailsTab';
import {
    fetchBenefitPlanById,
    createBenefitPlan,
    updateBenefitPlan,
} from '../../services/benefitPlanService';
import { fetchVendors } from '../../services/systemService';
import { validateUrl } from '../../utils/validate';
import { PlanTypeEnum } from '../../utils/data/enums';
import { deepEqual } from '../../utils/utils';
import { ROUTES } from '../../constants/routes';
import { getText } from '../../i18n';
const TABS = {
    OVERVIEW: 'Plan Overview',
    HEADER_DISPLAY: 'Header Display',
    FILES: 'Files',
    PLAN_DETAILS: 'Plan Details',
};

const BenefitPlanTabsPage = ({ isCreating = false }) => {
    const { showModal } = useContext(ModalContext);
    const navigate = useNavigate();
    const { benefitPlanId } = useParams();
    const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);
    const [planData, setPlanData] = useState({
        id: 0,
        name: '',
        code: '',
        planTypeId: null,
        planTypeName: '',
        networkTypeId: null,
        carrier: '',
        policy: '',
        phoneNumber: '',
        url: '',
        providerList: '',
        enabled: true,
        overrideName: '',
        headerText: '',
        benefitPlanAssets: [],
    });
    const [originalPlanData, setOriginalPlanData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [highlightedField, setHighlightedField] = useState(null);

    const [clearDetailsTrigger, setClearDetailsTrigger] = useState(0);

    const [hasPlanDetails, setHasPlanDetails] = useState(false);

    const detailsTabRef = useRef(null);

    useEffect(() => {
        if (benefitPlanId && !isCreating) {
            loadPlanData(benefitPlanId);
        } else {
            setIsLoading(false);
        }
        fetchVendors();
    }, [benefitPlanId, isCreating]);

    const loadPlanData = async id => {
        try {
            setIsLoading(true);
            const fetchedPlan = await fetchBenefitPlanById(id);

            setPlanData(fetchedPlan);
            setOriginalPlanData(JSON.parse(JSON.stringify(fetchedPlan)));
            setHasPlanDetails(
                fetchedPlan.planDetails && fetchedPlan.planDetails.length > 0,
            );
        } catch (error) {
            console.error('Failed to fetch benefit plan data', error);
            Toast.error(getText('msg_673b36a43380'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabChange = tab => {
        setActiveTab(tab);
    };

    const handlePlanDataChange = updatedData => {
        setPlanData(prevData => ({
            ...prevData,
            ...updatedData,
        }));
    };

    const validatePlanData = () => {
        const errors = [];
        const invalidFields = [];

        if (!planData.name || !planData.name.trim()) {
            errors.push({
                message: getText('msg_09cdd83c9272'),
                tab: TABS.OVERVIEW,
                field: 'name',
            });
            invalidFields.push('name');
        }

        if (!planData.code || !planData.code.trim()) {
            errors.push({
                message: getText('msg_d0cf451a4482'),
                tab: TABS.OVERVIEW,
                field: 'code',
            });
            invalidFields.push('code');
        }

        if (!planData.planTypeId) {
            errors.push({
                message: getText('msg_59eb2a036368'),
                tab: TABS.OVERVIEW,
                field: 'planTypeId',
            });
            invalidFields.push('planTypeId');
        }

        if (!planData.vendorId) {
            errors.push({
                message: getText('msg_dcd01e8b47be'),
                tab: TABS.OVERVIEW,
                field: 'vendorId',
            });
            invalidFields.push('vendorId');
        }

        if (planData.url && planData.url.trim()) {
            const urlValidation = validateUrl(planData.url.trim());
            if (!urlValidation.isValid) {
                errors.push({
                    message: urlValidation.error,
                    tab: TABS.OVERVIEW,
                    field: 'url',
                });
                invalidFields.push('url');
            }
        }

        if (planData.providerList && planData.providerList.trim()) {
            const urlValidation = validateUrl(planData.providerList.trim());
            if (!urlValidation.isValid) {
                errors.push({
                    message: urlValidation.error,
                    tab: TABS.OVERVIEW,
                    field: 'providerList',
                });
                invalidFields.push('providerList');
            }
        }

        if (planData.code && planData.code.length > 128) {
            errors.push({
                message: getText('msg_4d6769eb9816'),
                tab: TABS.OVERVIEW,
                field: 'code',
            });
            if (!invalidFields.includes('code')) {
                invalidFields.push('code');
            }
        }

        if (planData.name && planData.name.length > 100) {
            errors.push({
                message: getText('msg_84f9a115d29a'),
                tab: TABS.OVERVIEW,
                field: 'name',
            });
            if (!invalidFields.includes('name')) {
                invalidFields.push('name');
            }
        }

        if (planData.overrideName && planData.overrideName.length > 100) {
            errors.push({
                message: getText('msg_d8033dbab6e9'),
                tab: TABS.OVERVIEW,
                field: 'overrideName',
            });
            invalidFields.push('overrideName');
        }

        if (planData.policy && planData.policy.length > 100) {
            errors.push({
                message: getText('msg_2b3c17dd5c99'),
                tab: TABS.OVERVIEW,
                field: 'policy',
            });
            invalidFields.push('policy');
        }

        if (planData.phoneNumber && planData.phoneNumber.length > 20) {
            errors.push({
                message: getText('msg_439ccb1f6879'),
                tab: TABS.OVERVIEW,
                field: 'phoneNumber',
            });
            invalidFields.push('phoneNumber');
        }

        if (planData.url && planData.url.length > 255) {
            errors.push({
                message: getText('msg_a1a52d4fc086'),
                tab: TABS.OVERVIEW,
                field: 'url',
            });
            if (!invalidFields.includes('url')) {
                invalidFields.push('url');
            }
        }

        if (planData.providerList && planData.providerList.length > 255) {
            errors.push({
                message: getText('msg_620aa3bf6b2b'),
                tab: TABS.OVERVIEW,
                field: 'providerList',
            });
            if (!invalidFields.includes('providerList')) {
                invalidFields.push('providerList');
            }
        }

        if (errors.length > 0) {
            if (errors.length === 1) {
                Toast.error(errors[0].message);
            } else {
                Toast.error(
                    getText('msg_e0030e1601dc'),
                );
            }

            if (invalidFields.length > 0) {
                setHighlightedField(invalidFields[0]);
            }
            return false;
        }

        setHighlightedField(null);
        return true;
    };

    /**
     * Removes the "+" from phone numbers within the plan data payload.
     * @param {object} data - The plan data payload.
     * @returns {object} - The updated plan data payload with "+" removed from phone numbers.
     */
    const removePlusFromPhoneNumbers = data => {
        const updatedData = { ...data };
        if (updatedData.phoneNumber) {
            updatedData.phoneNumber = updatedData.phoneNumber.replace('+', '');
        }
        return updatedData;
    };

    const assignDefaultHeaderDisplayValueByPlanType = data => {
        const updatedData = { ...data };
        if (updatedData.planTypeId && !updatedData.headerDisplay) {
            switch (updatedData.planTypeId) {
                case PlanTypeEnum.Accident:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/Accident.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Accident</span></strong></div>
                        <div>Your accident plan offers coverage for accidents and injuries and benefits are paid based on the injury you receive and the treatment you need. Coverage is for off-the-job accidents only and may include broken bones, burns, concussions, eye injuries, or cuts Requiring Stitches.</div>
                    `;
                    break;
                case PlanTypeEnum.BasicLife:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/Basic Life.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Basic Life and AD&D</span></strong></div>
                        <div>Basic Life and Accidental Death & Dismemberment (AD&D) policy is available at no cost to you.</div>
                        <div>In the event of death, or if you suffer dismemberment as a result of an accident during active employment, the benefit will be paid to someone you name as your beneficiary.</div>
                    `;
                    break;
                case PlanTypeEnum.CriticalIllness:
                case PlanTypeEnum.CriticalIllness2:
                case PlanTypeEnum.CriticalIllness3:
                case PlanTypeEnum.CriticalIllness4:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/Critical Illness.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Critical Illness</span></strong></div>
                        <div>Coping with a Critical Illness such as a heart attack, stroke or cancer can be a financial hardship. Your Critical Illness policy helps cover unexpected expenses such as medical deductibles and copays, home healthcare costs, child care or mortgage payments/rent and home maintenance.</div>
                    `;
                    break;
                case PlanTypeEnum.Dental:
                    updatedData.headerDisplay = `<img src="/benefitPlan/Dental.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">`;
                    break;
                case PlanTypeEnum.DependentCareFSA:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/DCFSA.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Dependent Care Flexible Spending Account (FSA)</span></strong></div>
                        <div>This FSA allows you pay for qualified out-of-pocket dependent care expenses on a pre-tax basis.</div>
                        <div>Examples of eligible expenses include daycare for children and adults, summer day camp, and after school programs.</div>
                    `;
                    break;
                case PlanTypeEnum.HealthcareFSA:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/HCFSA.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Health Care FSA (HCFSA)</span></strong></div>
                        <div>Your Flexible Spending Account (FSA) allows you to use pre-tax dollars to pay for eligible medical, dental, vision and pharmacy expenses incurred by you or any other person whom you claim as a dependent on your federal income tax return.</div>
                        <div><br></div>
                        <div><strong>Eligible Expenses:</strong></div>
                        <ul>
                            <li>
                                <div>Medical expenses: co-pays, co-insurance, and deductibles</div>
                            </li>
                            <li>
                                <div>Dental expenses: exams, cleanings, X-rays, and braces</div>
                            </li>
                            <li>
                                <div>Vision expenses: exams, contact lenses and supplies, eyeglasses, and laser eye surgery</div>
                            </li>
                            <li>
                                <div>Professional services: physical therapy, chiropractor, and acupuncture</div>
                            </li>
                            <li>
                                <div>Prescription drugs, insulin, and prescribed over-the-counter medicine</div>
                            </li>
                            <li>
                                <div>Over-the-counter health care items: bandages, blood pressure monitors, etc.</div>
                            </li>
                        </ul>
                        <div>FSAs are subject to the “use it or lose it” rule so it's important to plan carefully. Although you are allowed to **carry over up to $500** of unused funds into the next benefit plan year, you generally must use the money in an FSA account *within* the plan year.</div>
                    `;
                    break;
                case PlanTypeEnum.HospitalIndemnity:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/Hospital.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Hospital Indemnity Coverage</span></strong></div>
                        <div>Hospital Indemnity coverage provides benefits if you or your insured dependents are confined in a hospital as an inpatient because of a covered accidental injury or a covered sickness. The benefit can help cover expenses such as a medical deductible, coinsurance and copays, travel, food and lodging expenses, child care plus everyday expenses like utilities and groceries.</div>
                    `;
                    break;
                case PlanTypeEnum.HRA:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/HRA.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Health Reimbursement Arrangement (HRA)</span></strong></div>
                        <div>As an enrolled participant, your HRA helps you pay for eligible medical expenses using employer-funded dollars. You can use your HRA funds tax-free to cover qualified healthcare costs, reducing your out-of-pocket expenses. Be sure to keep track of your available balance and submit claims for reimbursement as needed.</div>
                    `;
                    break;
                case PlanTypeEnum.HSA:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/HSA.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Health Savings Account (HSA)</span></strong></div>
                        <div>If you're enrolled in our High Deductible Health Plan, you can take advantage of a Health Savings Account (HSA) — a powerful, tax-advantaged way to save for qualified medical expenses. With an HSA, you can:</div>
                        <ul>
                            <li>
                                <div>Contribute pre-tax dollars directly from your paycheck, reducing your taxable income</div>
                            </li>
                            <li>
                                <div>Enjoy tax-free growth on your account balance through interest or investment earnings</div>
                            </li>
                            <li>
                                <div>Withdraw funds tax-free at any time to pay for eligible medical, dental, and vision expenses</div>
                            </li>
                            <li>
                                <div>Carry over unused funds year to year, giving you a flexible way to save for future healthcare needs</div>
                            </li>
                        </ul>
                        <div>An HSA not only helps you manage healthcare costs today but also serves as a smart savings tool for your long-term health and financial well-being.</div>
                    `;
                    break;
                case PlanTypeEnum.IdentityTheft:
                    updatedData.headerDisplay = `<img src="/benefitPlan/Identity Theft.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">`;
                    break;
                case PlanTypeEnum.Legal:
                    updatedData.headerDisplay = `<img src="/benefitPlan/Legal.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">`;
                    break;
                case PlanTypeEnum.LimitedPurposeFSA:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/LPFSA.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Limited Purpose Flexible Spending Account (LPFSA)</span></strong></div>
                        <div>This option is for employees enrolled in a Health Savings Accounts (HSA).</div>
                        <div>A Limited Purpose FSA allows you to set aside pre-tax money for preventive care, dental and vision related expenses. Amounts unused at the end of the claims period are forfeited so it's important to plan carefully, the same as with other health care FSAs.</div>
                    `;
                    break;
                case PlanTypeEnum.LTD:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/LTD.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Long Term Disability (LTD)</span></strong></div>
                        <div>This policy protects you from loss of income in the event you are unable to work due to illness, injury or accident for an extended period of time.</div>
                    `;
                    break;
                case PlanTypeEnum.Medical:
                    updatedData.headerDisplay = `<img src="/benefitPlan/Medical.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">`;
                    break;
                case PlanTypeEnum.ParkingTransitFSA:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/Parking Transit FSA.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Parking & Commuter Benefit</span></strong></div>
                        <div>Set aside a portion of your pay on a pre-tax basis for mass transit (bus, rail & ferry), van pools and commuter highway vehicles as well as qualified fees incurred for parking at or near work, parking at or near a transportation service site and park and ride expenses.</div>
                    `;
                    break;
                case PlanTypeEnum.STD:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/Short Term Disability.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>    
                        <div><strong><span style="color: #2F2B43;">Short Term Disability (STD)</span></strong></div>
                        <div>This policy pays you a portion of your income for a short period of time when you experience a disability for a qualifying health condition.</div>
                    `;
                    break;
                case PlanTypeEnum.Vision:
                    updatedData.headerDisplay = `<img src="/benefitPlan/Vision.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">`;
                    break;
                case PlanTypeEnum.VolChildLife:
                    updatedData.headerDisplay = `<img src="/benefitPlan/Vol Life - Child.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">`;
                    break;
                case PlanTypeEnum.VolEELife:
                    updatedData.headerDisplay = `
                        <img src="/benefitPlan/Vol Life EE.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">
                        <div></div>
                        <div><strong><span style="color: #2F2B43;">Voluntary Life and AD&D</span></strong></div>
                        <div>Voluntary Life and Accidental Death & Dismemberment (AD&D) policy is available at no cost to you.</div>
                        <div>In the event of death, or if you suffer dismemberment as a result of an accident during active employment, the benefit will be paid to someone you name as your beneficiary.</div>
                    `;
                    break;
                case PlanTypeEnum.VolLTD:
                    updatedData.headerDisplay = `<img src="/benefitPlan/Vol LTD.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">`;
                    break;
                case PlanTypeEnum.VolSTD:
                    updatedData.headerDisplay = `<img src="/benefitPlan/Vol STD.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">`;
                    break;
                case PlanTypeEnum.VolSpouseLife:
                    updatedData.headerDisplay = `<img src="/benefitPlan/Vol Life - Spouse.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">`;
                    break;
                case PlanTypeEnum.WholeLife:
                    updatedData.headerDisplay = `<img src="/benefitPlan/Whole Life.png" style="width: 100%; height: auto; cursor: pointer;" draggable="true">`;
                    break;
                default:
                    break;
            }
        }
        return updatedData;
    };

    const sanitizeCurrencyValue = val => {
        if (val == null || val === '') return null;
        const cleaned = val.toString().replace(/[$,]/g, '').trim();
        if (cleaned === '') return null;
        return cleaned;
    };

    const sanitizePercentValue = val => {
        if (val == null || val === '') return null;
        const cleaned = val.toString().replace(/[%\,]/g, '').trim();
        if (cleaned === '') return null;
        return cleaned;
    };

    const handleSave = async () => {
        if (!validatePlanData()) {
            return;
        }

        if (detailsTabRef.current) {
            const isValid = detailsTabRef.current.validateAllDetails();
            if (!isValid) {
                return;
            }
        }

        try {
            setIsSaving(true);

            const sanitizedPlanDetails = !hasPlanDetails
                ? []
                : planData.planDetails.map(detail => {
                      const sanitizeCurrencyValue = val => {
                          if (val == null || val === '') return null;
                          const cleaned = val
                              .toString()
                              .replace(/[$,]/g, '')
                              .trim();
                          if (cleaned === '') return null;
                          return cleaned;
                      };

                      const sanitizePercentValue = val => {
                          if (val == null || val === '') return null;
                          const cleaned = val
                              .toString()
                              .replace(/[%\,]/g, '')
                              .trim();
                          if (cleaned === '') return null;
                          return cleaned;
                      };

                      switch (detail.planDetailsValueTypeId) {
                          case VALUE_TYPE.Currency:
                              return {
                                  ...detail,
                                  inNetworkValue: sanitizeCurrencyValue(
                                      detail.inNetworkValue,
                                  ),
                                  outNetworkValue: sanitizeCurrencyValue(
                                      detail.outNetworkValue,
                                  ),
                              };
                          case VALUE_TYPE.Percent:
                              return {
                                  ...detail,
                                  inNetworkValue: sanitizePercentValue(
                                      detail.inNetworkValue,
                                  ),
                                  outNetworkValue: sanitizePercentValue(
                                      detail.outNetworkValue,
                                  ),
                              };
                          default:
                              return detail;
                      }
                  });

            const processedPlanData = {
                ...planData,
                planDetails: sanitizedPlanDetails,
            };

            const planDataWithDefaultHeaderDisplayValues =
                assignDefaultHeaderDisplayValueByPlanType(processedPlanData);
            const finalPlanData = removePlusFromPhoneNumbers(
                planDataWithDefaultHeaderDisplayValues,
            );

            if (isCreating || !benefitPlanId) {
                await createBenefitPlan(finalPlanData);
                Toast.success(getText('msg_8ef025bb998e'));
                navigate(ROUTES.BENEFIT_PLANS);
            } else {
                await updateBenefitPlan(finalPlanData);
                // reload the plan so all of the IDs are set (avoid duplicates)
                await loadPlanData(finalPlanData.id);
                setActiveTab(TABS.OVERVIEW); // go back to the overview tab
                Toast.success(getText('msg_e4d5ebcc81eb'));
            }
        } catch (error) {
            console.error('Failed to save benefit plan', error);
            const errorMessage =
                error.response?.data?.detail ||
                'There was an issue saving the benefit plan data.';
            Toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (checkUnsavedChanges()) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: () => navigate(ROUTES.BENEFIT_PLANS),
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_36fff63ccbcd'),
            });
        } else {
            navigate(ROUTES.BENEFIT_PLANS);
        }
    };

    const checkUnsavedChanges = () => {
        if (!originalPlanData) {
            if (!isCreating) return false;

            return (
                !!planData.name ||
                !!planData.code ||
                !!planData.planTypeId ||
                !!planData.networkTypeId ||
                !!planData.vendorId ||
                !!planData.policy
            );
        }

        return !deepEqual(planData, originalPlanData);
    };

    const handleClearDetails = () => {
        setClearDetailsTrigger(prev => prev + 1);
    };

    return (
        <div className={styles.pageFill}>
            <div className={`${styles.pageContentFill} page-content`}>
                <div className={`${styles.cardFill} card`}>
                    <div className={`${styles.flexGrow1} card-body px-0 py-4`}>
                        <div className="d-flex justify-content-between align-items-center position-relative pb-3 flex-column flex-xl-row gap-2">
                            <h2 className="page-title float-start ms-4 m-0 col-auto col-lg-2 text-nowrap align-self-xl-baseline">
                                {isCreating
                                    ? 'Add Benefit Plan'
                                    : planData.name}
                            </h2>
                            <div
                                className={`${styles.fitContentWidth} col-auto col-lg-2 d-flex me-3 align-self-xl-baseline justify-content-end`}
                            >
                                {isSaving ? (
                                    <span className={styles.ltrText}>
                                        {getText('msg_ae7e887517b0')}
                                    </span>
                                ) : (
                                    <div className="float-end d-flex align-items-center align-self-baseline">
                                        {!isCreating &&
                                            hasPlanDetails &&
                                            activeTab == TABS.PLAN_DETAILS && (
                                                <span
                                                    type="button"
                                                    onClick={handleClearDetails}
                                                    disabled={isSaving}
                                                    className="ing-text-buttons mx-3"
                                                    tabIndex={11}
                                                >
                                                    <i className="bi bi-arrow-repeat me-2" />
                                                    {getText('msg_06e00d016b1f')}
                                                </span>
                                            )}
                                        <span
                                            type="button"
                                            onClick={handleSave}
                                            disabled={isSaving}
                                            className="ing-text-buttons mx-3"
                                            tabIndex={12}
                                        >
                                            <i className="bi bi-check me-2" />
                                            {getText('msg_efc007a393f6')}
                                        </span>
                                        <span
                                            type="button"
                                            onClick={handleClose}
                                            disabled={isSaving}
                                            className="ing-text-buttons mx-3"
                                            tabIndex={13}
                                        >
                                            <i className="bi bi-x me-2" />
                                            {getText('msg_bbfa773e5a63')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {!isCreating && (
                            <div className="d-flex justify-content-between align-items-center position-relative mt-5">
                                <ul
                                    className={`col-md-8 col-lg-8 ${styles.tabList}`}
                                >
                                    {Object.values(TABS).map(tab => (
                                        <li
                                            key={tab}
                                            className={`${styles.tabItem} ${
                                                activeTab === tab
                                                    ? styles.active
                                                    : ''
                                            }`}
                                            onClick={() => handleTabChange(tab)}
                                        >
                                            {tab}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {isLoading ? (
                            <div className="w-100 mt-5 pb-5 text-center">
                                <Spinner
                                    width={50}
                                    className={styles.loadingSpinner}
                                />
                            </div>
                        ) : (
                            <div
                                className={`w-100 mt-5 pb-5 ${styles.tabContainer}`}
                            >
                                {activeTab === TABS.OVERVIEW && (
                                    <BenefitPlanOverviewTab
                                        data={planData}
                                        onChange={handlePlanDataChange}
                                        onSave={handleSave}
                                        highlightedField={highlightedField}
                                        onFieldFocus={() =>
                                            setHighlightedField(null)
                                        }
                                    />
                                )}
                                {activeTab === TABS.HEADER_DISPLAY && (
                                    <BenefitPlanHeaderDisplayTab
                                        data={planData}
                                        onChange={handlePlanDataChange}
                                        onSave={handleSave}
                                    />
                                )}
                                {activeTab === TABS.FILES && (
                                    <BenefitPlanFilesTab
                                        data={planData}
                                        onChange={handlePlanDataChange}
                                        onSave={handleSave}
                                    />
                                )}
                                {activeTab === TABS.PLAN_DETAILS && (
                                    <BenefitPlanDetailsTab
                                        ref={detailsTabRef}
                                        data={planData}
                                        onChange={handlePlanDataChange}
                                        clearTrigger={clearDetailsTrigger}
                                        onSave={handleSave}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BenefitPlanTabsPage;
